import { orderBy } from 'lodash';
import { action, computed, observable } from 'mobx';
import { observer } from 'mobx-react';
import {
    autoinject,
    bind,
    deepStringFind,
    errorRedirect,
    inject,
    ModalHandle,
    NothingHere,
    TableHeadOptions,
    SubTopBar,
    Title,
    toArray,
} from 'Mri.Web.Common';
import * as React from 'react';
import { BsCardHeading } from '@react-icons/all-files/bs/BsCardHeading';
import { Input } from 'reactstrap';
import { CreateOrEditModelDefinition } from './CreateOrEditModelDefinition';
import { ModelDefinitionItem } from './ModelDefinitionItem';
import { ModelDefinitionsStore } from './ModelDefinitionsStore';

@autoinject
@observer
export class ModelDefinitionList extends React.Component {
    @action
    public async componentDidMount() {
        this.loading = true;
        try {
            await this.modelDefinitionsStore.loadAllAsync();
        } catch (e: any) {
            errorRedirect(e);
        } finally {
            this.loading = false;
        }
    }

    public render() {
        return (
            <div className="container-fluid">
                <Title>Model Definitions</Title>

                <SubTopBar>
                    <button className="btn btn-primary" onClick={this.modalHandle.open}>
                        Create Model Definition
                    </button>

                    <div className="mx-2 border" />

                    <Input
                        type="text"
                        value={this.searchValue}
                        onChange={this.onSearchInputChange}
                        className="mw-450"
                        placeholder="Search Model Definitions..."
                    />
                </SubTopBar>

                <div className="row">
                    <div className="col-12 px-0">
                        <table className="table table-striped w-100 h-100">
                            <thead>
                                <tr>
                                    <th className="align-middle">
                                        Name
                                        <TableHeadOptions
                                            value={this.sort}
                                            field="modelNumber"
                                            onChange={this.onSortChange}
                                            exclusive
                                        />
                                    </th>
                                    <th className="align-middle">
                                        Part Number
                                        <TableHeadOptions
                                            value={this.sort}
                                            field="partNumber"
                                            onChange={this.onSortChange}
                                            exclusive
                                        />
                                    </th>
                                    <th className="align-middle">Description</th>
                                    <th className="align-middle">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {this.modelDefinitions.map(m => (
                                    <ModelDefinitionItem modelDefinition={m} key={m.modelDefinitionGuid} />
                                ))}
                            </tbody>
                        </table>

                        <NothingHere
                            empty={this.modelDefinitions.length <= 0}
                            icon={<BsCardHeading />}
                            loading={this.loading}
                        />
                    </div>
                </div>

                <CreateOrEditModelDefinition modalHandle={this.modalHandle} viewState="create" />
            </div>
        );
    }

    @bind
    @action
    private async onSortChange(value: string[]) {
        this.sort = value;
    }

    @bind
    @action
    private async onSearchInputChange(event: React.FormEvent<HTMLInputElement>) {
        const target = event.target as HTMLInputElement;
        this.searchValue = target.value;
    }

    @computed
    private get modelDefinitions() {
        let models = toArray(this.modelDefinitionsStore.modelDefinitions);

        if (this.searchValue) {
            models = models!.filter(c => {
                const s = {
                    config: c,
                };

                return deepStringFind(s, this.searchValue);
            });
        }
        if (this.sort.length) {
            const orders = this.sort.map(s => s.split(' '));
            const keys = orders.map(s => s[0]);
            const directions = orders.map(s => (s.length > 1 && s[1] == 'desc' ? 'desc' : 'asc'));
            models = orderBy(models, keys, directions);
        }

        return models;
    }

    private modalHandle = new ModalHandle();
    @inject
    private readonly modelDefinitionsStore!: ModelDefinitionsStore;
    @observable private sort: string[] = [];
    @observable private searchValue: string = '';
    @observable private loading = false;
}
